import { useRef } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { FiltersDefinition, FiltersState, OneFilterPicker } from '@factorialco/f0-react'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { useLocalStorage } from 'usehooks-ts'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import LogicOperatorForm from 'modules/core/components/MultiFilters/Forms/LogicOperatorForm'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import LocationSelector from 'modules/shift_management/components/ShiftManagement/Content/TableHeader/CommandsFilterBar/LocationSelector'
import { useMultiFilterSelector } from 'modules/shift_management/hooks/filters/useMultiFilterSelector'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useUI } from 'modules/shift_management/hooks/useUI'
import { transformFilterStateFormat } from 'modules/shift_management/lib/multiFilters/transformFilterFormat'

const MultiFiltersSelector = () => {
  const [, dispatchUI] = useUI()
  const { company } = useCurrentSession()
  const [value, setValue] = useLocalStorage<FiltersState<FiltersDefinition<string>>>(
    `${LOCAL_STORAGE_KEYS.shiftManagementNewMultiFilters}_${company.id}`,
    {}
  )
  const appliedFiltersRef = useRef<boolean>(false)
  const [{ locationIds, employeeIds, teamIds, appliedFromAi }, dispatchFilters] = useFilters()
  const { filtersDefinition, filterKeys, shiftsConflicts, view, statsLoading } =
    useMultiFilterSelector({ locationIds })
  const [, dispatchPagination] = usePagination()

  const lastAppliedAiKey = useRef<string | null>(null)

  useUnsafeEffect(() => {
    if (!appliedFromAi || statsLoading) return

    const aiKey = `e:${(employeeIds ?? []).join(',')}|t:${(teamIds ?? []).join(',')}`
    if (aiKey === lastAppliedAiKey.current) return
    lastAppliedAiKey.current = aiKey

    const aiValue: FiltersState<FiltersDefinition<string>> = {}
    if (employeeIds && employeeIds.length > 0) aiValue[filterKeys.employees] = employeeIds
    if (teamIds && teamIds.length > 0) aiValue[filterKeys.teams] = teamIds

    handleChange(aiValue)
  }, [statsLoading, appliedFromAi, employeeIds, teamIds]) // eslint-disable-line react-hooks/exhaustive-deps

  useUnsafeEffect(() => {
    if (statsLoading || appliedFiltersRef.current) return
    if (appliedFromAi) return

    const hasAnyFilters = Object.values(filterKeys).some((filterKey) => {
      const filterValue = value[filterKey]

      if (Array.isArray(filterValue)) return filterValue.length > 0
      if (filterValue == null) return false
      if (typeof filterValue === 'string') return filterValue.length > 0
      if (typeof filterValue === 'boolean') return filterValue

      return true
    })

    if (!hasAnyFilters) return

    handleChange(value)
  }, [statsLoading, appliedFromAi]) // eslint-disable-line react-hooks/exhaustive-deps

  useUnsafeEffect(() => {
    appliedFiltersRef.current = false
    lastAppliedAiKey.current = null
  }, [view.startAt, view.endAt])

  const handleChange = (fValue: FiltersState<FiltersDefinition<string>>) => {
    setValue(fValue)
    const transformedFilters = transformFilterStateFormat(fValue, filterKeys, shiftsConflicts)

    dispatchFilters({
      type: 'FILTER_CUSTOM',
      id: null,
      employeeIds: transformedFilters.employeeIds,
      shiftIds: transformedFilters.shiftIds,
      withoutShiftIds: transformedFilters.withoutShiftIds,
      customFilters: transformedFilters.customFilters,
      logicOperator: new LogicOperatorForm({ logic_operator: 'or' }),
      terminated: transformedFilters.terminated,
      teamIds: transformedFilters.teamIds,
      legalEntityIds: transformedFilters.legalEntityIds,
    })

    dispatchUI({ type: 'LOADING' })
    dispatchPagination({ type: 'RESET_PAGINATION' })
    appliedFiltersRef.current = true
  }

  return (
    <Box flexDirection="row" gap="s8" alignItems="center" minWidth="none">
      <Box width="s200">
        <LocationSelector />
      </Box>
      <OneFilterPicker
        filters={filtersDefinition}
        value={value}
        onChange={handleChange}
        mode="simple"
        displayCounter
      />
    </Box>
  )
}

export { MultiFiltersSelector }
